/* PastéBCN — Product purchase
   Web Component <pb-product-purchase>:
   - Mantiene la selección de variante (cards radio) y actualiza precio,
     iconos de logística (ya están en cada card), línea de fulfillment
     dinámica y nudge de recuperación para Grande.
   - Hidden input [name=id] del form se sincroniza con la variante
     elegida → Dawn <product-form> hace el AJAX add-to-cart con el id
     correcto, sin necesidad de <product-info> ni re-render de sección.
   - Sobrevive a shopify:section:load (custom element + guards).
   - Emite evento custom 'pb:variant-change' para que la galería pueda
     hacer image swap si la variante lleva featured_image. */

if (!customElements.get('pb-product-purchase')) {
  class PbProductPurchase extends HTMLElement {
    connectedCallback() {
      this.cards = Array.from(this.querySelectorAll('[data-variant-id]'));
      this.radios = Array.from(this.querySelectorAll('[data-variant-radio]'));
      this.variantIdInput = this.querySelector('[data-variant-id-input]');
      this.priceEl = this.querySelector('[data-variant-price]');
      this.fulfillmentEl = this.querySelector('[data-fulfillment-line]');
      this.nudgeEl = this.querySelector('[data-nudge]');
      this.submitBtn = this.querySelector('[data-add-to-cart]');

      let dataNode = this.querySelector('[data-variants-json]');
      try {
        this.variants = dataNode ? JSON.parse(dataNode.textContent) : [];
      } catch (_) {
        this.variants = [];
      }

      this.config = {
        fulfillmentDefault: this.dataset.fulfillmentDefault || '',
        fulfillmentOnlyBcn: this.dataset.fulfillmentOnlyBcn || '',
        nudgeText: this.dataset.nudgeText || '',
        moneyFormat: window.PB_MONEY_FORMAT || '€{{amount}}',
        soldOutLabel: this.dataset.soldOutLabel || 'Agotado',
        addLabel: this.dataset.addLabel || 'Añadir al carrito',
      };

      this._onRadioChange = this._onRadioChange.bind(this);
      this.radios.forEach((r) => r.addEventListener('change', this._onRadioChange));

      /* Estado inicial: aplica al checked actual */
      const initialChecked = this.radios.find((r) => r.checked) || this.radios[0];
      if (initialChecked) this._applyVariant(initialChecked.value, { silent: true });
    }

    disconnectedCallback() {
      this.radios.forEach((r) => r.removeEventListener('change', this._onRadioChange));
    }

    _onRadioChange(e) {
      this._applyVariant(e.target.value);
    }

    _applyVariant(variantId, { silent = false } = {}) {
      const variant = this.variants.find((v) => String(v.id) === String(variantId));
      if (!variant) return;

      /* 1) Hidden input para que el form mande la variante correcta */
      if (this.variantIdInput) this.variantIdInput.value = variant.id;

      /* 2) Marcar la card como seleccionada (clase visual) */
      this.cards.forEach((c) => {
        const on = String(c.dataset.variantId) === String(variant.id);
        c.classList.toggle('is-selected', on);
      });

      /* 3) Precio */
      if (this.priceEl) {
        this.priceEl.textContent = this._formatMoney(variant.price);
      }

      /* 4) Línea de fulfillment dinámica */
      if (this.fulfillmentEl) {
        const card = this.cards.find((c) => String(c.dataset.variantId) === String(variant.id));
        const onlyBcn = card && card.dataset.onlyBcn === 'true';
        this.fulfillmentEl.textContent = onlyBcn ? this.config.fulfillmentOnlyBcn : this.config.fulfillmentDefault;
      }

      /* 5) Nudge: solo visible para variantes only_bcn (Grande) */
      if (this.nudgeEl) {
        const card = this.cards.find((c) => String(c.dataset.variantId) === String(variant.id));
        const showNudge = card && card.dataset.onlyBcn === 'true';
        this.nudgeEl.hidden = !showNudge;
      }

      /* 6) Estado del botón ATC (disabled si sold out) */
      if (this.submitBtn) {
        if (variant.available === false) {
          this.submitBtn.disabled = true;
          this.submitBtn.setAttribute('aria-disabled', 'true');
          this._setSubmitText(this.config.soldOutLabel);
        } else {
          this.submitBtn.disabled = false;
          this.submitBtn.removeAttribute('aria-disabled');
          this._setSubmitText(this.config.addLabel);
        }
      }

      /* 7) Galería swap si la variante tiene featured_media */
      if (!silent && variant.featured_media) {
        document.dispatchEvent(
          new CustomEvent('pb:variant-change', {
            detail: { variant_id: variant.id, featured_media_id: variant.featured_media.id },
          })
        );
      }
    }

    _setSubmitText(text) {
      if (!this.submitBtn) return;
      const span = this.submitBtn.querySelector('[data-add-to-cart-text]');
      if (span) span.textContent = text;
      else this.submitBtn.textContent = text;
    }

    _formatMoney(cents) {
      /* Si Shopify.formatMoney está disponible (cargado por theme.liquid
         vía Shopify common JS), lo usamos. Si no, fallback simple. */
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        return window.Shopify.formatMoney(cents, this.config.moneyFormat);
      }
      const value = (cents / 100).toFixed(2).replace('.', ',');
      return value + ' €';
    }
  }

  customElements.define('pb-product-purchase', PbProductPurchase);
}
